/**
 * This component contains code to add a comment on graph.
 */
import {
  Typography,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  IconButton,
  DialogActions
} from "@mui/material";
import { AddCommentFields } from "./AddComment.types";
import { ReactComponent as EditCommentIcon } from "assets/icons/editComment.svg";
import { useTranslation } from "react-i18next";
import Close from "@mui/icons-material/Close";
import DashedDivider from "common/ui/DashedDivider";
import { useAddNewComment } from "services/runs.service";
import useForm from "hooks/useForm";
import dayjs from "dayjs";
import { useParams } from "react-router";
import * as constants from "helpers/constants";
import queryClient from "helpers/queryClient";
import LoadingButton from "common/ui/LoadingButton";
import { encode } from "utils/common";
import Save from "@mui/icons-material/Save";
import { selectAddComment } from "context/modal.selector";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { setAddComment } from "context/modal.context";
import toast from "react-hot-toast";

function AddComment() {
  const { t } = useTranslation();
  const props = useAppSelector(selectAddComment);
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    clearErrors
  } = useForm<AddCommentFields>();

  const { runId } = useParams();

  const addNewComment = useAddNewComment(onCommentAdded);

  async function onCommentAdded(_data, error) {
    await queryClient.invalidateQueries({
      queryKey: ["run-comments"]
    });
    await queryClient.invalidateQueries({
      queryKey: ["run-details"]
    });
    let message = t("addComment.successMessage");

    if (!error) {
      toast.success(message);
    }

    clearErrors();
    reset();

    dismiss();
  }

  function onModalDismiss() {
    clearErrors();
    reset();
    dismiss();
  }

  function dismiss() {
    dispatch(
      setAddComment({
        open: false,
        runCommentTime: null,
        runId: runId
      })
    );
  }

  function onSubmit(data: any) {
    addNewComment.mutate({
      data: {
        comment: encode(data.comment),
        runCommentTime: dayjs(props.runCommentTime).toISOString()
      },
      runId: props.runId
    });
  }

  return (
    <>
      <Dialog open={props.open} scroll="body" className="highlight">
        <DialogTitle sx={{ paddingBottom: 0 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <EditCommentIcon />
              <Typography
                color="primary.main"
                variant="h6"
                fontWeight="medium"
                alignSelf="center"
              >
                {t("addComment.comment")}
              </Typography>
            </Stack>
            <Stack direction="row">
              <Typography
                color="gray.main"
                variant="subtitle1"
                alignSelf="center"
                sx={{ marginLeft: 5 }}
              >
                {t("addComment.dateTime")}
              </Typography>
              <Typography
                color="black"
                variant="subtitle1"
                alignSelf="center"
                sx={{ marginLeft: 2 }}
              >
                {dayjs(props.runCommentTime).format(constants.DATE_TIME_FORMAT)}
              </Typography>
              <IconButton onClick={onModalDismiss}>
                <Close />
              </IconButton>
            </Stack>
          </Stack>
          <DashedDivider />
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register("comment", {
                required: t("addComment.required"),
                pattern: {
                  value: /^$|\S/,
                  message: t("addComment.required")
                }
              })}
              helperText={errors.comment?.message}
              error={!!errors.comment}
              variant="outlined"
              rows={5}
              multiline={true}
              fullWidth
              sx={{ mb: 2, mt: 2 }}
            />

            <DashedDivider />
            <DialogActions
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              <LoadingButton
                loading={addNewComment.isLoading}
                variant="outlined"
                color="primary"
                type="submit"
                startIcon={<Save />}
              >
                {t("common.save")}
              </LoadingButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AddComment;
